import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { fmtInt } from '../lib/format'

/**
 * Newsletters — one row per title.
 *
 * The counts are the ones an operator acts on: confirmed is the audience,
 * pending is the funnel's leak, and unsynced explains a confirmed
 * subscriber who is on no Mailgun list and would hear nothing from a send
 * driven by the list rather than by our own table.
 */

type TitleSummary = {
  publication: string
  name: string
  send_day: string
  enabled: boolean
  list_address: string
  confirmed: number
  pending: number
  unsubscribed: number
  unsynced: number
}

export function NewslettersPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['newsletters'],
    queryFn: () => api.get<{ titles: TitleSummary[] }>('/api/admin/newsletters'),
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Execution · email</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Newsletters
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">per-title identity &amp; lists</span>
          </h1>
        </div>
      </header>

      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}

      {!isLoading && data && (
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-4 font-normal text-left">Title</th>
                <th className="label py-2 px-3 font-normal text-left">Newsletter</th>
                <th className="label py-2 px-3 font-normal text-left">Day</th>
                <th className="label py-2 px-3 font-normal text-left">On</th>
                <th className="label py-2 px-3 font-normal text-right">Confirmed</th>
                <th className="label py-2 px-3 font-normal text-right">Pending</th>
                <th className="label py-2 px-3 font-normal text-right">Unsynced</th>
                <th className="label py-2 px-3 font-normal text-left"></th>
              </tr>
            </thead>
            <tbody>
              {data.titles.map((t) => (
                <tr key={t.publication} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                  <td className="px-4 py-2"><PubBadge slug={t.publication} /></td>
                  <td className="px-3 py-2 text-paper">{t.name}</td>
                  <td className="px-3 py-2 text-paper-dim">{t.send_day}</td>
                  <td className={`px-3 py-2 ${t.enabled ? 'text-radar-green' : 'text-paper-trace'}`}>
                    {t.enabled ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.confirmed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-paper-dim">{fmtInt(t.pending)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {t.unsynced > 0 ? (
                      <span className="text-radar-red">{fmtInt(t.unsynced)} — no list</span>
                    ) : (
                      <span className="text-paper-trace">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Link to={`/newsletters/${t.publication}/subscribers`} className="text-paper-dim hover:text-amber-glow">
                      Subscribers
                    </Link>
                    <span className="text-paper-trace mx-2">·</span>
                    <Link to={`/newsletters/${t.publication}/settings`} className="text-paper-dim hover:text-amber-glow">
                      Settings
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
